const LOG_LEVEL_VALUES = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
} as const;

export type LogLevel = keyof typeof LOG_LEVEL_VALUES;

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  [key: string]: unknown;
}

export interface Logger {
  error(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  debug(message: string, context?: Record<string, unknown>): void;
  trace(message: string, context?: Record<string, unknown>): void;
  child(context: Record<string, unknown>): Logger;
}

export interface LoggerOptions {
  level?: LogLevel;
  defaultContext?: Record<string, unknown>;
  output?: (entry: LogEntry) => void;
}

function getDefaultLogLevel(): LogLevel {
  try {
    if (typeof process !== 'undefined' && process.env) {
      const envLevel = process.env['LOG_LEVEL']?.toLowerCase();
      if (envLevel && isValidLogLevel(envLevel)) return envLevel;
      if (process.env['NODE_ENV'] === 'development') return 'debug';
    }
  } catch {
    // Running in a context without process (e.g., browser without polyfill)
  }
  return 'info';
}

function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(value, (_k, v: unknown) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  });
}

function defaultOutput(entry: LogEntry): void {
  let json: string;
  try {
    json = JSON.stringify(entry);
  } catch {
    // AWS SDK errors and similar objects can contain circular references
    // (e.g. err.protocol.serdeContext loops); fall back to a circular-safe
    // serializer so the log line is preserved instead of dropping the entry.
    json = safeStringify(entry);
  }
  switch (entry.level) {
    case 'error':
      // eslint-disable-next-line no-console
      console.error(json);
      break;
    case 'warn':
      // eslint-disable-next-line no-console
      console.warn(json);
      break;
    default:
      // eslint-disable-next-line no-console
      console.log(json);
      break;
  }
}

export function isValidLogLevel(value: string): value is LogLevel {
  return value in LOG_LEVEL_VALUES;
}

export function createLogger(options?: LoggerOptions): Logger {
  const level = options?.level ?? getDefaultLogLevel();
  const defaultContext = options?.defaultContext ?? {};
  const output = options?.output ?? defaultOutput;

  function shouldLog(msgLevel: LogLevel): boolean {
    return LOG_LEVEL_VALUES[msgLevel] <= LOG_LEVEL_VALUES[level];
  }

  function log(
    msgLevel: LogLevel,
    message: string,
    context?: Record<string, unknown>,
  ): void {
    if (!shouldLog(msgLevel)) return;

    const entry: LogEntry = {
      level: msgLevel,
      message,
      timestamp: new Date().toISOString(),
      ...defaultContext,
      ...context,
    };

    output(entry);
  }

  return {
    error: (message, context) => log('error', message, context),
    warn: (message, context) => log('warn', message, context),
    info: (message, context) => log('info', message, context),
    debug: (message, context) => log('debug', message, context),
    trace: (message, context) => log('trace', message, context),
    child: (childContext) =>
      createLogger({
        level,
        defaultContext: { ...defaultContext, ...childContext },
        output,
      }),
  };
}

export const logger = createLogger();
